import React, { useState } from 'react';
import {
	Request,
	Maybe,
	useSimulateVanCreditMutation,
	SimulateVanCreditMutation,
	SimulateVanCreditInput,
	useTiieBanxicoQuery
} from '@/graphql/__generated__/van-credits';
import { GRAY_COLORS } from '@/UIlibrary/globalTheme';
import SimulationTerms from './VanCreditSimulation/SimulationTerms';
import SimulationInputs from './VanCreditSimulation/SimulationInputs';
import SimulationTable from './VanCreditSimulation/SimulationTable';
import { useTranslations } from 'use-intl';
import { useSimulationStore } from '@/stores/useVanCreditSimulationStore';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import Button from '@/UIlibrary/Button/Button';
import FinancialIndicators from './VanCreditSimulation/FinancialIndicators';
import EvaluationMatrix from './VanCreditSimulation/EvaluationMatrix';

interface VanCreditSimulationProps {
	requestData: Maybe<Request> | undefined;
	refetchData: () => Promise<any>;
}

const VanCreditSimulation: React.FC<VanCreditSimulationProps> = ({ requestData, refetchData }) => {
	const t = useTranslations('VanCreditRequestSimulation');
	const vanCreditsClient = useApolloClient(SCHEMAS.VAN_CREDITS);
	const { formData } = useSimulationStore();
	const [formInputErrors, setFormInputErrors] = useState<Record<string, string>>();

	const [simulateVanCredit, { loading: loadingSimulation }] = useSimulateVanCreditMutation({
		client: vanCreditsClient,
		onCompleted: async (data: SimulateVanCreditMutation) => {
			const simulationData = data.simulateVanCredit;
			if (simulationData?.successful) {
				toast.success(t('SimulationSuccess'));
				await refetchData();
			}
			if (simulationData?.mutationErrors && simulationData.mutationErrors.length > 0) {
				Sentry.captureException(simulationData?.mutationErrors, {
					extra: {
						requestId: requestData?.requestId
					}
				});
				simulationData.mutationErrors.forEach((error) => {
					if (error) {
						switch (error.__typename) {
							case 'InvalidInput':
								setFormInputErrors((prev) => ({
									...prev,
									[error.field]: error.message
								}));
								break;
							case 'InvalidAction':
								toast.error(t('SimulationError'));
								break;
							case 'ActionFailed':
								toast.error(error.message, { duration: 15000 });
								break;
							default:
								Sentry.captureException(new Error(`Unexpected error type: ${error}`), {
									extra: {
										requestId: requestData?.requestId,
										error
									}
								});
								toast.error(t('SimulationError'));
								break;
						}
					}
				});
				return;
			}
		},
		onError: () => {
			toast.error(t('SimulationError'));
		}
	});

	const { data: tiieBanxicoData, loading: tiieBanxicoLoading } = useTiieBanxicoQuery({
		client: vanCreditsClient
	});

	const handleSimulation = async () => {
		if (!requestData?.requestId) {
			toast.error('Missing van credit request ID');
			return;
		}

		const ensureMoneyFormat = (value: string): string => {
			if (!value) return '0.00 MXN';
			let cleanValue = value;

			// Removing currency symbols and spaces
			cleanValue = cleanValue.replace(/[$MX\s]/g, '');
			// Removing MXN if present
			cleanValue = cleanValue.replace(/MXN/g, '');
			// Removing commas (thousands separators)
			cleanValue = cleanValue.replace(/,/g, '');

			const numValue = parseFloat(cleanValue);
			return isNaN(numValue) ? '0.00 MXN' : `${numValue.toFixed(2)} MXN`;
		};

		const ensureDecimalFormat = (value: string): string => {
			const numValue = parseFloat(value);
			if (isNaN(numValue)) return '0.0';
			return numValue.toFixed(2);
		};

		const input: SimulateVanCreditInput = {
			requestId: requestData.requestId,
			amountRequested: ensureMoneyFormat(formData.amountRequested),
			paymentCapacity: ensureMoneyFormat(formData.paymentCapacity),
			daysPerPeriod: formData.daysPerPeriod,
			basisPoints: ensureDecimalFormat(formData.basisPoints),
			...(formData.gpsAmount &&
				ensureMoneyFormat(formData.gpsAmount) !== '0.00 MXN' && {
					gpsAmount: ensureMoneyFormat(formData.gpsAmount)
				}),
			...(formData.insuranceAmount &&
				ensureMoneyFormat(formData.insuranceAmount) !== '0.00 MXN' && {
					insuranceAmount: ensureMoneyFormat(formData.insuranceAmount)
				}),
			firstPaymentDate: formData.firstPaymentDate,
			sameDayPayment: formData.sameDayPayment,
			percentageOfCreditToSettle: ensureDecimalFormat(formData.percentageOfCreditToSettle)
		};

		console.log('Simulation input:', input); // Debug log

		await simulateVanCredit({
			variables: { input }
		});
	};

	if (!requestData) {
		return null;
	}

	if (loadingSimulation || tiieBanxicoLoading) {
		return (
			<div className="flex justify-center items-center h-screen">
				<div className="loader"></div>
			</div>
		);
	}

	return (
		<div
			className="flex flex-col rounded-lg mt-5 w-full"
			style={{ backgroundColor: GRAY_COLORS.gray50 }}
		>
			<SimulationInputs
				data={requestData}
				inputErrors={formInputErrors}
				tiieBanxico={tiieBanxicoData?.tiieBanxicoValue}
			/>

			<SimulationTerms
				loading={false}
				tiieBanxico={tiieBanxicoData?.tiieBanxicoValue?.value || ''}
				highestAmountToApprove={
					requestData?.simulation?.configuration?.highestAmountToApprove || undefined
				}
				generatedAt={requestData?.simulation?.generatedAt || undefined}
				minimumPaymentToSettleTheCreditTotal={
					requestData?.simulation?.configuration?.minimumPaymentToSettleTheCreditTotal || undefined
				}
				minimumPaymentToSettleTheCreditPercentage={
					requestData?.simulation?.configuration?.minimumPaymentToSettleTheCreditPercentage || ''
				}
			/>

			<div className="flex items-center justify-center px-6">
				<div className="flex flex-col gap-8 items-center justify-center pb-4 pt-8 bg-white w-full">
					<div className="w-3/4 flex border-t-2 border-gray-200" />
					<Button
						label={t('DoSimulation')}
						size="auto"
						onClick={handleSimulation}
						disabled={loadingSimulation}
					/>
				</div>
			</div>

			<SimulationTable loading={false} data={requestData.simulation} />

			<FinancialIndicators
				economicSolvencyI={requestData?.economicSolvencyI}
				vehicleValue={requestData?.vehicleValue}
				recoveryTimeInYears={requestData?.recoveryTimeInYears}
				economicSolvencyIi={requestData?.economicSolvencyIi}
				businessComprehensiveOperativeEfficiency={
					requestData?.businessComprehensiveOperativeEfficiency
				}
				applicantComprehensiveOperativeEfficiency={
					requestData?.applicantComprehensiveOperativeEfficiency
				}
				calculatedNetIncomeIndex={requestData?.calculatedNetIncomeIndex}
			/>

			<EvaluationMatrix
				requestData={requestData}
				refetchData={refetchData}
				requestId={requestData.requestId || ''}
			/>
		</div>
	);
};

export default VanCreditSimulation;
